import React from 'react';
import { motion } from 'framer-motion';
import { Coffee, Heart, Leaf } from 'lucide-react';
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.4,
      ease: 'easeIn'
    }
  }
};
const values = [{
  icon: <Coffee className="w-8 h-8" data-id="element-158" />,
  title: 'Uncompromising Quality',
  desc: 'We meticulously source the finest beans and ingredients, ensuring every item we serve meets our exacting standards for taste and freshness.'
}, {
  icon: <Heart className="w-8 h-8" data-id="element-159" />,
  title: 'Community First',
  desc: 'More than a café, we are a gathering place. We strive to foster connections and create a welcoming space for everyone who walks through our doors.'
}, {
  icon: <Leaf className="w-8 h-8" data-id="element-160" />,
  title: 'Sustainable Practices',
  desc: 'From compostable packaging to ethically sourced coffee and local partnerships, we are committed to minimizing our environmental footprint.'
}];
export function About() {
  return <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="w-full" data-id="element-161">
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden" data-id="element-162">
        <div className="absolute inset-0 z-0" data-id="element-163">
          <div className="absolute inset-0 bg-cafe-900/60 z-10" data-id="element-164"></div>
          <img src="https://images.unsplash.com/photo-1442512595331-e89e73853f31?auto=format&fit=crop&q=80&w=2000" alt="Barista making coffee" className="w-full h-full object-cover" data-id="element-165" />
        </div>
        <div className="relative z-10 text-center px-6" data-id="element-166">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="font-serif text-5xl md:text-6xl font-bold text-white mb-4" data-id="element-167">
            Our Story
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="text-cafe-100 text-lg md:text-xl max-w-2xl mx-auto" data-id="element-168">
            Crafting moments of joy, one cup at a time.
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-cafe-50" data-id="element-169">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-170">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-171">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-172">
              <h2 className="font-serif text-4xl font-bold text-cafe-900 mb-6" data-id="element-173">
                From a Dream to Reality
              </h2>
              <div className="space-y-4 text-cafe-800/80 leading-relaxed" data-id="element-174">
                <p data-id="element-175">
                  Brew & Bloom was born out of a shared passion between two
                  friends, Emma and David, who believed that a neighborhood café
                  should be an extension of one's living room.
                </p>
                <p data-id="element-176">
                  After years of traveling and exploring coffee cultures around
                  the world, they returned home with a vision: to create a space
                  that combined the meticulous craft of specialty coffee with
                  the warmth of a local bakery.
                </p>
                <p data-id="element-177">
                  We opened our doors in 2024, and since then, we've been
                  dedicated to serving our community with ethically sourced
                  coffee, scratch-made pastries, and a genuine smile. Every
                  detail of our café, from the exposed brick walls to the custom
                  ceramic mugs, was chosen to make you feel right at home.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative" data-id="element-178">
              <div className="absolute inset-0 bg-cafe-sage rounded-lg transform translate-x-4 translate-y-4" data-id="element-179"></div>
              <img src="https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&q=80&w=1000" alt="Café founders" className="relative z-10 rounded-lg shadow-lg w-full h-auto" data-id="element-180" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-cafe-900 text-cafe-100" data-id="element-181">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-182">
          <div className="text-center mb-16" data-id="element-183">
            <h2 className="font-serif text-4xl font-bold mb-4" data-id="element-184">
              What We Believe In
            </h2>
            <p className="text-cafe-100/70 max-w-2xl mx-auto" data-id="element-185">
              The core principles that guide everything we do at Brew & Bloom.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-186">
            {values.map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.2
          }} className="bg-cafe-800/50 p-8 rounded-lg border border-cafe-800 hover:border-cafe-sage transition-colors text-center" data-id="element-187">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cafe-sage/20 text-cafe-sage mb-6" data-id="element-188">
                  {value.icon}
                </div>
                <h3 className="font-serif text-2xl font-bold mb-4" data-id="element-189">
                  {value.title}
                </h3>
                <p className="text-cafe-100/70 leading-relaxed" data-id="element-190">{value.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>
    </motion.div>;
}